import '@a11y/focus-trap';
import { Component, Element, Event, EventEmitter, Fragment, h, Method, Prop, State, Watch } from '@stencil/core';
import Bind from 'bind-decorator';

import { BreakPointDown, StyleCollection } from '../../types';
import { generateCssClasses } from '../../utils/generate-css-classes';
import { generateUniqueUuid } from '../../utils/generate-unique-uuid';
import { isInStorybook } from '../../utils/is-in-storybook';
import { toggleScrollLock } from '../../utils/scroll-lock';
import { hasSlotted } from '../../utils/slotted';

export interface ModalEventData {
	action?: string;
}
@Component({
	shadow: true,
	styleUrl: 'zds-modal.scss',
	tag: 'zds-modal',
})
export class ZdsModal {
	slotElements: {
		headerStart?: HTMLSlotElement;
		header?: HTMLSlotElement;
		headerEnd?: HTMLSlotElement;
		body?: HTMLSlotElement;
		footer?: HTMLSlotElement;
	} = {};
	bodyElement: HTMLDivElement;
	headerStartElement: HTMLDivElement;
	headerEndElement: HTMLDivElement;
	hideAction: string;
	isOpenChangedInternally: boolean;
	modalElement: HTMLDivElement;
	isInStorybook: boolean;

	@Element() hostElement: HTMLDivElement;

	@Event({ bubbles: true }) zdsModalHidden: EventEmitter<ModalEventData>;
	@Event({ bubbles: true }) zdsModalHide: EventEmitter<ModalEventData>;
	@Event({ bubbles: true }) zdsModalShown: EventEmitter<ModalEventData>;
	@Event({ bubbles: true }) zdsModalShow: EventEmitter<ModalEventData>;
	@Event({ bubbles: true }) zdsBackdropClick: EventEmitter;

	@Prop() noAutoClose: boolean = false;
	@Prop({ mutable: true }) fullscreen: BreakPointDown | boolean;
	@Prop() headerAlign: 'center' | 'start' | 'end' = 'center';
	@Prop() ignoreSB: boolean = false;
	@Prop({ mutable: true, reflect: true }) open: boolean;
	@Prop({ reflect: true }) zdsId: string = generateUniqueUuid('zds-modal');
	@Prop() zIndex: string;

	@State() isContentScrolledToTop: boolean = true;
	@State() isContentScrolledToBottom: boolean = false;
	@State() headerStartEndMinWidth: number;
	@State() isScrollable: boolean = false;

	@Watch('open')
	async onOpenChange(newValue: boolean, currentValue: boolean) {
		if (!newValue) {
			toggleScrollLock(false);
		}

		// if popout is opening and the content is scrolled, force it to scroll to top
		if (newValue && this.bodyElement.scrollTop > 0) {
			this.bodyElement.scrollTo({ top: 0, behavior: 'instant' });
		}

		// Ignore this change if it was done internally
		if (this.isOpenChangedInternally) {
			this.isOpenChangedInternally = false;
			return;
		}

		if (currentValue === newValue) {
			return;
		}

		if (newValue) {
			await this.show();
		} else {
			await this.hide('property-changed');
		}
	}

	@Watch('headerAlign')
	onHeaderAlignChange(newValue: ZdsModal['headerAlign']) {
		if (newValue === 'center') {
			this.headerStartEndMinWidth = Math.max(
				this.headerStartElement.offsetWidth,
				this.headerEndElement.offsetWidth,
			);
		} else {
			this.headerStartEndMinWidth = 0;
		}
	}

	@Bind
	@Method()
	async show() {
		toggleScrollLock(true);
		if (this.open) return;

		// make sure to delete any previously set hideActions
		delete this.hideAction;

		this.isOpenChangedInternally = true;
		this.open = true;

		this.addShadow();
		this.onModalBodyScroll();
		this.zdsModalShow.emit();
	}

	@Bind
	@Method()
	async hide(hideAction: string) {
		toggleScrollLock(false);
		if (!this.open) return;

		this.hideAction = hideAction;

		this.isOpenChangedInternally = true;
		this.open = false;
		this.bodyElement.classList.remove('has-shadow');
		this.zdsModalHide.emit(this.hideAction ? { action: this.hideAction } : {});
	}

	async componentDidLoad() {
		this.isInStorybook = !this.ignoreSB && isInStorybook();
		this.digestActions();

		if (this.open) {
			this.addShadow();

			await this.show();
		}

		if (this.headerStartElement && this.headerAlign === 'center') {
			// Ensure header-wrapper is centered in header, by enforcing a min-width on the header-start and header-end
			this.headerStartEndMinWidth = Math.max(
				this.headerStartElement.offsetWidth,
				this.headerEndElement.offsetWidth,
			);
		}
	}

	addShadow() {
		this.isScrollable = this.bodyElement.scrollHeight > this.bodyElement.offsetHeight;

		if (this.isScrollable) {
			this.bodyElement.classList.add('has-shadow');
		}
	}

	/**
	 * Children with the zds-modal-action attribute should hide the modal
	 */
	digestActions() {
		const actionButtons = this.hostElement.querySelectorAll('[zds-modal-action]');
		actionButtons.forEach((button) => {
			button.addEventListener('zdsClick', (ev: CustomEvent) => {
				if (this.open) {
					this.onModalActionClick(ev);
				}
			});
		});
	}

	async disconnectedCallback() {
		await this.hide('disconnected');
	}

	@Bind
	async onBackdropClosed() {
		if (!this.open) {
			return;
		}

		if (this.noAutoClose) {
			this.zdsBackdropClick.emit();

			return;
		}

		await this.hide('backdrop-closed');

		this.zdsBackdropClick.emit();
	}

	/**
	 * Clicking on a zds-modal-action element should store the reason why the modal hides, to be passed to the hide
	 * 	events, as well as hide the modal.
	 */
	@Bind
	async onModalActionClick(event: CustomEvent) {
		const modalHideAction = (event.target as Element).getAttribute('zds-modal-action');
		await this.hide(modalHideAction);
	}

	@Bind
	onModalBodyScroll() {
		this.isContentScrolledToTop = this.bodyElement.scrollTop === 0;

		// we need to floor this value in case there are decimal point values, ie: 323.5 === 323
		this.isContentScrolledToBottom =
			Math.floor(this.bodyElement.offsetHeight + this.bodyElement.scrollTop) === this.bodyElement.scrollHeight;
	}

	@Bind
	onTransitionEnd(event: TransitionEvent) {
		if (event.propertyName !== 'opacity') return;

		if (this.open) {
			this.zdsModalShown.emit();
		} else {
			this.zdsModalHidden.emit(this.hideAction ? { action: this.hideAction } : {});
		}
	}

	renderHeader() {
		// Note that we always need to have the header-start and header-end elements when center-aligning the header
		const hasHeader = hasSlotted(this.hostElement, 'header');
		const hasHeaderStart = hasSlotted(this.hostElement, 'header-start');
		const hasHeaderEnd = hasSlotted(this.hostElement, 'header-end');

		if (!hasHeaderStart && !hasHeader && !hasHeaderEnd) {
			return null;
		}

		const renderHeaderStart = hasHeaderStart || this.headerAlign === 'center';
		const renderHeaderEnd = hasHeaderEnd || this.headerAlign === 'center';

		const hideHeaderGap = !hasHeaderStart && !hasHeaderEnd;

		const headerStartEndStyle: StyleCollection = this.headerStartEndMinWidth
			? {
					minWidth: this.headerStartEndMinWidth + 'px',
				}
			: undefined;

		const headerClasses = generateCssClasses('zds-modal__title', {
			start: this.headerAlign === 'start',
			end: this.headerAlign === 'end',
		});

		return (
			<div class="zds-modal__header">
				{renderHeaderStart && !hideHeaderGap && (
					<div
						class="zds-modal__header-start"
						style={headerStartEndStyle}
						ref={(element) => (this.headerStartElement = element)}
					>
						<slot
							name="header-start"
							ref={(element: HTMLSlotElement) => {
								this.slotElements.headerStart = element;
							}}
						/>
					</div>
				)}
				<div class="zds-modal__header-wrapper">
					<h1 class={headerClasses} id={`${this.zdsId}-title`}>
						<slot
							name="header"
							ref={(element: HTMLSlotElement) => {
								this.slotElements.header = element;
							}}
						/>
					</h1>
				</div>
				{renderHeaderEnd && !hideHeaderGap && (
					<div
						class="zds-modal__header-end"
						style={headerStartEndStyle}
						ref={(element) => (this.headerEndElement = element)}
					>
						<slot
							name="header-end"
							ref={(element: HTMLSlotElement) => {
								this.slotElements.headerEnd = element;
							}}
						/>
					</div>
				)}
			</div>
		);
	}

	renderFooter() {
		if (!hasSlotted(this.hostElement, 'footer')) {
			return null;
		}

		return (
			<div class="zds-modal__footer">
				<slot
					name="footer"
					ref={(element: HTMLSlotElement) => {
						this.slotElements.footer = element;
					}}
				/>
			</div>
		);
	}

	render() {
		const getFullscreenState = () => {
			if (!this.fullscreen || (this.fullscreen as string) === 'false') {
				return {};
			} else if (this.fullscreen === true || (this.fullscreen as string) === 'true') {
				return {
					fullscreen: true,
				};
			} else {
				return {
					[`fullscreen-${this.fullscreen}`]: true,
				};
			}
		};

		const modalClasses = generateCssClasses('zds-modal', {
			open: this.open,
			storybook: this.isInStorybook,
			...getFullscreenState(),
		});

		const bodyCssClasses = generateCssClasses('zds-modal__body', {
			isScrolledToTop: this.isContentScrolledToTop,
			isScrolledToBottom: this.isContentScrolledToBottom,
		});

		const modalStyle = {
			zIndex: this.zIndex ?? '2001',
		};

		return (
			<Fragment>
				<zds-backdrop
					show={this.open || undefined}
					zIndex={2000}
					onZdsBackdropClosed={() => void this.onBackdropClosed()}
				/>
				<focus-trap>
					<div
						class={modalClasses}
						style={modalStyle}
						onTransitionEnd={this.onTransitionEnd}
						ref={(element) => (this.modalElement = element)}
					>
						<div
							class="zds-modal__wrapper"
							aria-labelledby={`${this.zdsId}-title`}
							aria-describedby={`${this.zdsId}-body`}
							aria-hidden={!this.open}
							role="dialog"
							aria-modal="true"
						>
							{this.renderHeader()}
							{this.renderHeader() && <zds-divider class="zds-modal__divider" />}
							<div
								id={`${this.zdsId}-body`}
								class={bodyCssClasses}
								ref={(element) => (this.bodyElement = element)}
								onScroll={this.onModalBodyScroll}
							>
								<slot
									name="body"
									ref={(element: HTMLSlotElement) => {
										this.slotElements.body = element;
									}}
								/>
							</div>
							{this.renderFooter() && <zds-divider class="zds-modal__divider" />}
							{this.renderFooter()}
						</div>
					</div>
				</focus-trap>
			</Fragment>
		);
	}
}
